// groupsTree API — Phase 2 트리 조회·이동·순서·자손·조상 엔드포인트 래퍼.
// GET  /api/groups/tree?kind=
// POST /api/groups/:id/move
// PUT  /api/groups/reorder
// GET  /api/groups/:id/descendants
// GET  /api/groups/:id/ancestors

import { api } from './client';
import type { GroupKind } from './types';

// ─── 타입 정의 ───────────────────────────────────────────────

/** 트리 노드 — 클라이언트 응답 형태 (서버 treeNodeToPublic 반환값). */
export interface GroupTreeNode {
  id: number;
  name: string;
  kind: GroupKind;
  /** 부모 그룹 id. null이면 forest 루트. */
  parent_id: number | null;
  /** 같은 부모 아래 형제 정렬 키. */
  order_index: number;
  color: string | null;
  icon: string | null;
  /** 자식 카운트 (children.length와 동일 — UI 표시용 캐시). */
  childCount: number;
  /** 자식 노드 배열 (재귀). */
  children: GroupTreeNode[];
}

/** 그룹 이동 입력 */
export interface MoveGroupInput {
  new_parent_id: number | null;
  new_order_index?: number;
}

/** reorder 입력 */
export interface ReorderGroupsInput {
  parent_id: number | null;
  ordered_group_ids: number[];
}

/** 이동 응답 — 서버 toPublic 반환값 (parent_id/order_index 미포함, 기존 GroupPublic 호환) */
export interface GroupPublicV2 {
  id: number;
  name: string;
  kind: GroupKind;
  color: string | null;
  icon: string | null;
  sortOrder: number;
  createdAt: number;
  updatedAt: number;
}

// ─── API 함수 ────────────────────────────────────────────────

export const groupsTreeApi = {
  /**
   * 트리 형태 그룹 목록 반환.
   * kind 미지정 시 전체 forest.
   */
  getTree: (kind?: GroupKind) => {
    const qs = kind ? `?kind=${kind}` : '';
    return api.get<GroupTreeNode[]>(`/api/groups/tree${qs}`);
  },

  /**
   * 그룹을 새 부모·위치로 이동 (원자적 트리 검증 포함).
   * 서버 에러 코드: group_cycle / group_depth_exceeded / group_kind_mismatch
   */
  moveGroup: (id: number, body: MoveGroupInput) =>
    api.post<GroupPublicV2>(`/api/groups/${id}/move`, body),

  /**
   * 같은 부모 아래 형제 순서 일괄 갱신.
   * parent_id=null 이면 루트 형제들 간 순서 조정.
   */
  reorderGroups: (body: ReorderGroupsInput) => api.put<{ ok: true }>('/api/groups/reorder', body),

  /**
   * 특정 그룹의 모든 자손 ID 배열 반환 (자기 자신 포함).
   * DnD 드롭 유효성 검사·descendants scope 미리 로드용.
   */
  descendants: (id: number) => api.get<{ ids: number[] }>(`/api/groups/${id}/descendants`),

  /**
   * 특정 그룹의 루트까지 조상 ID 배열 반환 (자기 자신 제외, breadcrumb용).
   */
  ancestors: (id: number) => api.get<{ ids: number[] }>(`/api/groups/${id}/ancestors`),
};
